import React, { useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import * as Yup from 'yup';
import i18n from 'i18n-js';
import Box from '@material-ui/core/Box';
import { BaseModal } from 'components/UI';
import { Formik, Form, Field } from 'formik';
import { TextField } from 'components/FormFields';
import { CustomersAction } from 'state/modules/customers';
import { UserGroupsSelect } from 'components/AsyncFields';

const InviteUserModal = ({ open, handleClick, onSuccess }) => {
  const dispatch = useDispatch();
  const formRef = useRef(null);
  const [CreateUserSchema] = useState(
    Yup.object().shape({
      user_group_id: Yup.number().required(i18n.t('common.required_field')),
      email: Yup.string()
        .max(250, i18n.t('common.max_character', { max: 250 }))
        .email(i18n.t('common.invalid_email'))
        .required(i18n.t('common.required_field')),
      first_name: Yup.string()
        .nullable()
        .max(50, i18n.t('common.max_character', { max: 50 })),
      last_name: Yup.string()
        .nullable()
        .max(50, i18n.t('common.max_character', { max: 50 })),
    })
  );

  const { inviteFormErrors, fetchingInviteUser } = useSelector((state) => state.customers);

  /**
   * Handle submit success
   */
  const handleSuccess = () => {
    if (onSuccess) onSuccess();
    handleClick();
  };

  /**
   * Function to be executed after form validation
   * @param {object} values Payload to invite customer
   */
  const onSubmit = (values) => {
    const { user_group_id, ...data } = values;

    let payload = { ...data };

    // remove empty fields
    Object.keys(payload).forEach((key) => !payload[key] && delete payload[key]);

    dispatch(CustomersAction.fetchInviteUser(user_group_id, payload, handleSuccess));
  };

  /**
   * Handle form submit
   */
  const handleSubmit = () => {
    formRef.current.submitForm();
  };

  return (
    <BaseModal
      size="sm"
      open={open}
      hasEnterButton
      hasCancelButton
      onCancel={() => {
        dispatch(CustomersAction.resetInviteFormErrors());
        handleClick();
      }}
      onEnter={handleSubmit}
      enableCloseButton={false}
      loading={fetchingInviteUser}
      title={i18n.t('users.invite')}
      enterButtonLabel={i18n.t('users.send_invite')}
      enterButtonProps={{ 'data-cy': 'invite-user' }}
    >
      <Formik
        innerRef={formRef}
        initialValues={{
          email: '',
          last_name: null,
          first_name: null,
          user_group_id: '',
        }}
        onSubmit={onSubmit}
        validationSchema={CreateUserSchema}
      >
        {({ handleSubmit }) => (
          <Form noValidate onSubmit={handleSubmit}>
            <Box mt={1}>
              <Field
                margin="none"
                required={true}
                name="user_group_id"
                component={UserGroupsSelect}
                label={i18n.t('users.user_group')}
                customParams={{ user_group_type: ['DRIVER'] }}
              />
            </Box>
            <Box mt={1}>
              <Field
                type="text"
                margin="none"
                required={false}
                name="first_name"
                component={TextField}
                label={i18n.t('fields.first_name')}
                asyncErrors={inviteFormErrors?.first_name}
              />
            </Box>
            <Box mt={1}>
              <Field
                type="text"
                margin="none"
                name="last_name"
                required={false}
                component={TextField}
                label={i18n.t('fields.last_name')}
                asyncErrors={inviteFormErrors?.last_name}
              />
            </Box>
            <Box mt={1}>
              <Field
                type="email"
                name="email"
                margin="none"
                component={TextField}
                label={i18n.t('fields.email')}
                asyncErrors={inviteFormErrors?.email}
              />
            </Box>
          </Form>
        )}
      </Formik>
    </BaseModal>
  );
};

InviteUserModal.propTypes = {
  onSuccess: PropTypes.func,
  open: PropTypes.bool.isRequired,
  handleClick: PropTypes.func.isRequired,
};

InviteUserModal.defaultProps = {
  open: false,
  handleClick: () => {},
};

export default InviteUserModal;
